import * as THREE from 'three';
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js';
import { clone as cloneSkinned } from 'three/addons/utils/SkeletonUtils.js';
import { createInkMaterial } from '../render/InkMaterial';

/** KayKit Adventurers pack (CC0, Kay Lousberg). */
export type CharacterKind = 'Knight' | 'Mage' | 'Rogue' | 'Barbarian';

/** Movement state that drives the animation blend. */
export type Pose = { speed: number; air: boolean; talking: boolean };

export type CharacterOptions = {
  /** Extra parts to show, e.g. 'Mug'. Weapons and shields are hidden by default. */
  keep?: string[];
  /** Hide hats/helmets so faces read better. */
  hideHeadgear?: boolean;
  /** Clip to loop when standing still (e.g. 'Sit_Floor_Idle' for NPCs). */
  idleClip?: string;
  height?: number;
  /** Corporate look: desaturate (0..1) and tint every part. */
  desaturate?: number;
  tint?: THREE.ColorRepresentation;
  /** Hide capes (modern folk don't wear them). */
  hideCape?: boolean;
};

const BODY_PARTS = ['Body', 'Head', 'ArmLeft', 'ArmRight', 'LegLeft', 'LegRight', 'Cape'];
const HEADGEAR = ['Helmet', 'Hat'];

const loader = new GLTFLoader();
const cache = new Map<CharacterKind, Promise<GLTF>>();

/**
 * Load one file at a time: parallel GLB parses intermittently fail to decode
 * their embedded textures in Chromium (blob fetch 404s).
 */
export async function preloadCharacters(kinds: CharacterKind[]): Promise<GLTF[]> {
  const out: GLTF[] = [];
  for (const k of kinds) out.push(await loadKind(k));
  return out;
}

function loadKind(kind: CharacterKind): Promise<GLTF> {
  let p = cache.get(kind);
  if (!p) {
    p = loader.loadAsync(`${import.meta.env.BASE_URL}models/kaykit-adventurers/${kind}.glb`);
    cache.set(kind, p);
  }
  return p;
}

/**
 * A rigged, animated character with ink shading. Each body part gets its own
 * outline id so the ink pass draws lines between cape, arms and body.
 */
export class Character {
  readonly root = new THREE.Group();
  private readonly mixer: THREE.AnimationMixer;
  private readonly actions = new Map<string, THREE.AnimationAction>();
  private readonly idle: THREE.AnimationAction;
  private readonly walk: THREE.AnimationAction;
  private readonly run: THREE.AnimationAction;
  private readonly air: THREE.AnimationAction;
  private readonly talk: THREE.AnimationAction | null;
  private walkW = 0;
  private runW = 0;
  private airW = 0;
  private talkW = 0;
  private oneShot: THREE.AnimationAction | null = null;
  private readonly model: THREE.Object3D;
  private readonly materials: THREE.ShaderMaterial[] = [];
  private readonly held: Partial<Record<'weapon' | 'shield' | 'carry', THREE.Object3D>> = {};

  private constructor(gltf: GLTF, opts: CharacterOptions) {
    const model = cloneSkinned(gltf.scene) as THREE.Object3D;
    this.model = model;
    const keep = new Set(opts.keep ?? []);
    model.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      const part = BODY_PARTS.some((p) => mesh.name.endsWith('_' + p));
      const headgear = HEADGEAR.some((h) => mesh.name.endsWith('_' + h));
      const cape = mesh.name.endsWith('_Cape');
      mesh.visible = (part && !(cape && opts.hideCape)) || keep.has(mesh.name) || (headgear && !opts.hideHeadgear);
      const src = mesh.material as THREE.MeshStandardMaterial;
      mesh.material = createInkMaterial({
        map: src.map ?? null,
        band: [0.12, 0.2],
        shadowValue: 0.62,
        lineBreak: 0.05,
        lineScale: 16,
        tone: 0,
      });
      const u = (mesh.material as THREE.ShaderMaterial).uniforms;
      if (opts.desaturate) u.uDesat.value = opts.desaturate;
      if (opts.tint !== undefined) u.uTint.value.set(opts.tint);
      this.materials.push(mesh.material as THREE.ShaderMaterial);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.frustumCulled = false;
    });

    // Normalise height so every pack lines up with our physics capsule.
    const box = new THREE.Box3().setFromObject(model);
    const h = box.max.y - box.min.y;
    model.scale.setScalar((opts.height ?? 1.3) / h);
    model.position.y = -box.min.y * model.scale.y;
    this.root.add(model);

    this.mixer = new THREE.AnimationMixer(model);
    for (const clip of gltf.animations) this.actions.set(clip.name, this.mixer.clipAction(clip));
    const pick = (...names: string[]) => names.map((n) => this.actions.get(n)).find(Boolean)!;
    this.idle = pick(opts.idleClip ?? 'Idle', 'Idle', 'Unarmed_Idle');
    this.walk = pick('Walking_A', 'Walking_B');
    this.run = pick('Running_A', 'Running_B');
    this.air = pick('Jump_Idle', 'Jump_Full_Short');
    this.talk = this.actions.get('Interact') ?? null;
    for (const a of [this.idle, this.walk, this.run, this.air]) {
      a.play();
      a.setEffectiveWeight(0);
    }
    this.idle.setEffectiveWeight(1);
    this.idle.time = Math.random() * this.idle.getClip().duration;
  }

  static async create(kind: CharacterKind, opts: CharacterOptions = {}): Promise<Character> {
    return new Character(await loadKind(kind), opts);
  }

  /** Flash white (hit feedback): 1 = full white, eases out on its own in update(). */
  flash(amount = 1): void {
    this.flashAmt = amount;
  }
  private flashAmt = 0;

  /** Find a bone by name (GLTFLoader strips dots, so 'handslot.r' becomes 'handslotr'). */
  bone(name: string): THREE.Object3D | null {
    const alt = name.replace(/[.:/[\]]/g, '');
    return this.model.getObjectByName(name) ?? this.model.getObjectByName(alt) ?? null;
  }

  /** Put an item in a hand (weapon = right, shield = left, carry = both, over the head). */
  hold(slot: 'weapon' | 'shield' | 'carry', obj: THREE.Object3D | null): void {
    this.held[slot]?.removeFromParent();
    delete this.held[slot];
    if (!obj) return;
    const bone = this.bone(slot === 'shield' ? 'handslot.l' : 'handslot.r');
    if (!bone) return;
    if (slot === 'carry') {
      // Carried crates ride on the head so the arms can keep swinging.
      const head = this.bone('head') ?? bone;
      head.add(obj);
      obj.position.set(0, 0.9, 0);
    } else {
      bone.add(obj);
    }
    obj.traverse((o) => { o.castShadow = true; });
    this.held[slot] = obj;
  }

  /** Play a clip once (e.g. 'Cheer', 'Jump_Land'), then fall back to the blend. */
  playOnce(name: string, fade = 0.15): void {
    const a = this.actions.get(name);
    if (!a) return;
    this.oneShot?.fadeOut(fade);
    a.reset().setLoop(THREE.LoopOnce, 1).fadeIn(fade).play();
    a.clampWhenFinished = false;
    this.oneShot = a;
  }

  update(dt: number, _t: number, pose: Pose): void {
    if (this.dead) {
      this.mixer.update(dt);
      return;
    }
    const k = (rate: number) => 1 - Math.exp(-dt * rate);
    const moving = THREE.MathUtils.clamp(pose.speed / 2.0, 0, 1);
    const running = THREE.MathUtils.clamp((pose.speed - 3.0) / 1.2, 0, 1);
    this.walkW += ((pose.air ? 0 : moving) - this.walkW) * k(10);
    this.runW += ((pose.air ? 0 : running) - this.runW) * k(8);
    this.airW += ((pose.air ? 1 : 0) - this.airW) * k(12);
    this.talkW += ((pose.talking ? 1 : 0) - this.talkW) * k(6);

    let shot = 0;
    if (this.oneShot) {
      const a = this.oneShot;
      const left = a.getClip().duration - a.time;
      shot = a.isRunning() ? Math.min(1, left / 0.15) : 0;
      if (!a.isRunning()) this.oneShot = null;
    }
    const free = 1 - shot;
    const ground = (1 - this.airW) * free;
    this.air.setEffectiveWeight(this.airW * free);
    this.run.setEffectiveWeight(ground * this.walkW * this.runW);
    this.walk.setEffectiveWeight(ground * this.walkW * (1 - this.runW));
    this.idle.setEffectiveWeight(ground * (1 - this.walkW) * (1 - this.talkW * 0.6));
    if (this.talk) {
      if (this.talkW > 0.01 && !this.talk.isRunning()) this.talk.play();
      this.talk.setEffectiveWeight(ground * (1 - this.walkW) * this.talkW * 0.6);
    }
    // Keep feet from sliding: playback speed follows ground speed.
    this.walk.timeScale = THREE.MathUtils.clamp(pose.speed / 2.4, 0.6, 1.6);
    this.run.timeScale = THREE.MathUtils.clamp(pose.speed / 4.6, 0.8, 1.4);
    this.mixer.update(dt);
    if (this.flashAmt > 0 || this.materials[0]?.uniforms.uFlash.value) {
      this.flashAmt = Math.max(0, this.flashAmt - dt * 5);
      for (const m of this.materials) m.uniforms.uFlash.value = this.flashAmt * 0.8;
    }
  }

  /** Stop all looping clips and play a final pose (death). */
  playFinal(name: string): void {
    const a = this.actions.get(name);
    if (!a) return;
    this.mixer.stopAllAction();
    a.reset().setLoop(THREE.LoopOnce, 1).play();
    a.clampWhenFinished = true;
    this.oneShot = null;
    this.dead = true;
  }

  /** Bring a character back from playFinal(). */
  revive(): void {
    this.dead = false;
    this.mixer.stopAllAction();
    for (const a of [this.idle, this.walk, this.run, this.air]) {
      a.reset().play();
      a.setEffectiveWeight(0);
    }
    this.idle.setEffectiveWeight(1);
  }
  dead = false;
}
